import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FlaskConical } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "../../services/supabase";
import { EyeIcon, EyeOffIcon } from "lucide-react";

import { returnToSourceIfRequested } from "@/lib/return-to";
import { AUTH_PAGE_COPY, FIELD_LABELS, TOAST_TITLES } from "@/lib/ui-labels";
import { loginSchema, LoginData } from "@/lib/form-schemas";
import { API_PATHS } from "@shared/platform/app-metadata";

export default function AuthPage() {
  const [_, navigate] = useLocation();
  const { user, loginMutation } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);

  const loginForm = useForm<LoginData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    const checkAndClearSession = async () => {
      try {
        const res = await fetch(API_PATHS.user);
        
        if (res.status === 401) {
          console.log('Not authenticated, normal login flow will continue');
        } 
        else if (res.status === 200) {
          console.log('User appears to be authenticated already');
          const userData = await res.json();
          console.log('User data found:', userData.username);
        }
        else {
          console.warn('Unexpected API response, clearing session cookies to be safe');
          document.cookie = AUTH_PAGE_COPY.connectSidExpiresThu01Jan;
        }
      } catch (err) {
        console.error('Error checking authentication status:', err);
        document.cookie = AUTH_PAGE_COPY.connectSidExpiresThu01Jan;
      }
      
      setIsLoading(false);
    };
    
    checkAndClearSession();
  }, []);

  const handlePasswordResetRequest = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSendingReset(true);
    setError(null);

    try {
      console.log(`[Password Reset] Requesting reset for email: ${resetEmail}`);
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/update-password`,
      });

      if (error) {
        console.error("[Password Reset] Supabase error:", error);
        if (error.message.includes(AUTH_PAGE_COPY.userNotFound)) {
           throw new Error(AUTH_PAGE_COPY.noUserFoundWithThisEmail);
        }
        throw error;
      }

      console.log("[Password Reset] Success! Email sent.");
      toast({
        title: TOAST_TITLES.passwordResetSent,
        description: AUTH_PAGE_COPY.ifAnAccountExistsForAn(resetEmail),
      });
      setIsResetDialogOpen(false);
      setResetEmail("");

    } catch (err: any) {
      console.error("[Password Reset] Caught error:", err);
      const errorMessage = err.error_description || err.message || AUTH_PAGE_COPY.anUnexpectedErrorOccurred;
      setError(errorMessage);
      toast({
        title: TOAST_TITLES.passwordResetFailed,
        description: AUTH_PAGE_COPY.failedToSendPasswordResetEmail(errorMessage),
        variant: "destructive",
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Auth page: Submitting login with:", loginForm.getValues().email);
    
    try {
      loginMutation.mutate(loginForm.getValues());
    } catch (err) {
      console.error("Login error:", err);
      toast({
        title: TOAST_TITLES.loginFailed,
        description: err instanceof Error ? err.message : AUTH_PAGE_COPY.unknownError,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (user && !returnToSourceIfRequested(window.location.search, (path) => navigate(path, { replace: true }))) {
      navigate("/");
    }
  }, [user, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <FlaskConical className="h-20 w-20 text-primary mr-1" />
              <h1 className="text-3xl font-bold">{AUTH_PAGE_COPY.chemicalInventoryManagementSystem}</h1>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Login</CardTitle>
              <CardDescription>
                {AUTH_PAGE_COPY.enterYourCredentialsToAccessYour}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...loginForm}>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <FormField
                    control={loginForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{FIELD_LABELS.email}</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder={AUTH_PAGE_COPY.enterYourEmail} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={loginForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{FIELD_LABELS.password}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input type={showPassword ? "text" : "password"} placeholder={AUTH_PAGE_COPY.enterYourPassword} {...field} />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                              {showPassword ? 
                                <EyeOffIcon className="h-4 w-4" /> : 
                                <EyeIcon className="h-4 w-4" />
                              }
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loginMutation.isPending}
                  >
                    {loginMutation.isPending ? AUTH_PAGE_COPY.loggingIn : "Login"}
                  </Button>
                </form>
                <div className="mt-4 text-center text-sm">
                  <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="link" className="h-11 px-3 font-normal">
                        {AUTH_PAGE_COPY.forgotPassword}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <form onSubmit={handlePasswordResetRequest}>
                        <DialogHeader>
                          <DialogTitle>{AUTH_PAGE_COPY.resetPassword}</DialogTitle>
                          <DialogDescription>
                            {AUTH_PAGE_COPY.enterYourEmailAddressBelowWe}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="reset-email" className="text-right">
                              Email
                            </Label>
                            <Input
                              id="reset-email"
                              type="email"
                              value={resetEmail}
                              onChange={(e) => setResetEmail(e.target.value)}
                              className="col-span-3"
                              placeholder="you@example.com"
                              required
                              disabled={isSendingReset}
                            />
                          </div>
                           {error && <p className="text-sm text-destructive col-span-4 text-center">{error}</p>}
                        </div>
                        <DialogFooter>
                          <Button type="submit" disabled={isSendingReset}>
                            {isSendingReset ? "Sending..." : AUTH_PAGE_COPY.sendResetLink}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <div className="hidden lg:flex flex-1 bg-primary items-center justify-center text-primary-foreground">
        <div className="max-w-md p-8">
          <h2 className="text-3xl font-bold mb-6">
            {AUTH_PAGE_COPY.chemicalInventoryManagementSystem}
          </h2>
          <p className="text-lg mb-6">
            {AUTH_PAGE_COPY.trackYourChemicalInventoryWithEase}
          </p>
          <ul className="space-y-2">
            <li className="flex items-center">
              <span className="h-2 w-2 rounded-full bg-primary-foreground mr-2" />
              {AUTH_PAGE_COPY.trackChemicalDetailsIncludingProductName}
            </li>
            <li className="flex items-center">
              <span className="h-2 w-2 rounded-full bg-primary-foreground mr-2" />
              {AUTH_PAGE_COPY.monitorInventoryAmountsAndLocations}
            </li>
            <li className="flex items-center">
              <span className="h-2 w-2 rounded-full bg-primary-foreground mr-2" />
              {AUTH_PAGE_COPY.detailedChangeHistoryForEveryModification}
            </li>
            <li className="flex items-center">
              <span className="h-2 w-2 rounded-full bg-primary-foreground mr-2" />
              {AUTH_PAGE_COPY.secureUserAuthenticationAndAccessControl}
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
